import { useMemo } from 'react';
import { type AccountResponse, AccountType } from '@shared/api/rest/accounts';
import { Carousel } from '@shared/widgets/carousel';
import { AccountTileSmall } from './tiles';
import { NoData, Spinner } from '@shared/widgets';

interface Props {
  accountType: AccountType;
  isLoading?: boolean;
  accounts: AccountResponse[];
  currency?: string;
}

export const AccountsGrid = (props: Props) => {
  const pagesWithData = useMemo(() => {
    const filteredAccounts = props.accounts.filter((x) => x.type === props.accountType);

    const mainAccounts = filteredAccounts.filter((account) => account.mainAccId === null);
    const subAccounts = filteredAccounts.filter((account) => account.mainAccId !== null);

    const grouped = mainAccounts.map((mainAccount) => {
      const relatedSubAccounts = subAccounts.filter((account) => account.mainAccId === mainAccount.id);
      return { mainAccount, subAccounts: relatedSubAccounts, isOrphanSub: false };
    });

    const orphanSubAccounts = subAccounts.filter((subAccount) => !mainAccounts.some((main) => main.id === subAccount.mainAccId));

    const allGroups = [
      ...grouped,
      ...orphanSubAccounts.map((subAccount) => ({
        mainAccount: subAccount,
        subAccounts: [],
        isOrphanSub: true,
      })),
    ];

    const countItemsPerPage = (document.getElementById('root')?.scrollWidth ?? 0 > 1280) ? 6 : 4;
    const countOfSlide = Math.ceil(allGroups.length / countItemsPerPage);

    const result: (typeof allGroups)[] = [];
    for (let i = 0; i < countOfSlide; i++) {
      const startIndex = i * countItemsPerPage;
      const endIndex = startIndex + countItemsPerPage;
      const page = allGroups.slice(startIndex, endIndex);
      result.push(page);
    }

    return result;
  }, [props.accounts, props.accountType]);

  if (props.isLoading) return <Spinner />;
  if (pagesWithData.length === 0) return <NoData />;
  return (
    <Carousel>
      {pagesWithData.map((itemsForPage, pageIndex) => (
        <div key={pageIndex} className='pr-4 !grid grid-cols-2 xl:grid-cols-3 gap-4 items-stretch'>
          {itemsForPage.map(({ mainAccount, subAccounts, isOrphanSub }) => (
            <AccountTileSmall
              key={mainAccount.id}
              account={mainAccount}
              subAccounts={subAccounts}
              isOrphanSub={isOrphanSub}
              currency={props.currency}
            />
          ))}
        </div>
      ))}
    </Carousel>
  );
};
